import { InfoCircledIcon } from '@radix-ui/react-icons'
import { useQueryClient } from '@tanstack/react-query'
import { useContext, useEffect, useState } from 'react'
import {
  type SubmitHandler,
  useForm,
} from 'react-hook-form'
import { TailSpin } from 'react-loading-icons'
import { toast } from 'sonner'

import AssigneesFilter from 'components/brokers/needs_attention/AssigneesFilter'
import WorkflowsContext from 'components/context/WorkflowsContext'
import AnimateHeightAndOpacity from 'components/reusable/AnimateHeightAndOpacity'
import TooltipWrapper from 'components/reusable/TooltipWrapper'
import { Button } from 'components/ui/button'
import { Checkbox } from 'components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select'
import { usePatchBrokersTaskWorkflows } from 'utils/web_api'

import { setQueryDataCallback } from '../needs_attention/utils'

export type BulkUpdateWorkflowsDialogProps = {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  selectedItems: string[]
}

type BulkUpdateWorkflowsFormInput = {
  broker_ids_to_add: string[]
  broker_ids_to_remove: string[]
  force_reassign_all_tasks?: boolean
  transition?: string
  cancellation_reason?: string
}

const transitionOptions = [
  { value: 'start', label: 'Start', description: 'Start pending workflows' },
  { value: 'complete_workflow', label: 'Complete', description: 'Mark workflows as completed' },
  { value: 'cancel_workflow', label: 'Cancel', description: 'Cancel workflows' },
  { value: 'pause', label: 'Pause', description: 'Pause in-progress workflows' },
  { value: 'resume', label: 'Resume', description: 'Resume paused workflows' },
  { value: 'reopen_workflow', label: 'Reopen', description: 'Reopen canceled workflows' },
]

const BulkUpdateWorkflowsDialog = ({
  isOpen,
  setIsOpen,
  selectedItems,
}: BulkUpdateWorkflowsDialogProps) => {
  const queryClient = useQueryClient()
  const { getWorkflowsQueryKey } = useContext(WorkflowsContext)

  const [assigneesValidationError, setAssigneesValidationError] = useState<string | null>(null)

  const { handleSubmit, setValue, watch, reset } = useForm<BulkUpdateWorkflowsFormInput>({
    defaultValues: {
      broker_ids_to_add: [],
      broker_ids_to_remove: [],
      force_reassign_all_tasks: false,
      transition: '',
      cancellation_reason: '',
    },
  })

  const toggleState = (
    key: 'broker_ids_to_add' | 'broker_ids_to_remove',
    value: string
  ) => {
    const currentValue = watch(key)
    if (currentValue.includes(value)) {
      setValue(key, currentValue.filter((v) => v !== value))
    } else {
      setValue(key, [...currentValue, value])
    }
  }

  const assigneesToAdd = watch('broker_ids_to_add')
  const assigneesToRemove = watch('broker_ids_to_remove')
  const forceReassignAll = watch('force_reassign_all_tasks')
  const selectedTransition = watch('transition')
  const cancellationReason = watch('cancellation_reason')

  const bulkUpdateMutation = usePatchBrokersTaskWorkflows()

  const handleOnOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (!open) {
      reset()
      setAssigneesValidationError(null)
    }
  }

  useEffect(() => {
    const conflict = assigneesToAdd.find((assignee) =>
      assigneesToRemove.includes(assignee)
    )

    if (conflict) {
      setAssigneesValidationError(
        'Assignees cannot be both added and removed. Please remove duplicate assignees.'
      )
    } else {
      setAssigneesValidationError(null)
    }
  }, [assigneesToAdd, assigneesToRemove])

  const handleCancel = () => {
    setIsOpen(false)
  }

  const onSubmit: SubmitHandler<BulkUpdateWorkflowsFormInput> = async (data) => {
    // Validate at least one action is specified
    const hasTransition = data.transition && data.transition.length > 0
    const hasAssignees = data.broker_ids_to_add.length > 0 || data.broker_ids_to_remove.length > 0

    if (!hasTransition && !hasAssignees) {
      toast.error('Please select a state transition or assign/remove brokers')
      return
    }

    // Validate cancellation reason if needed
    if (data.transition === 'cancel_workflow' && !data.cancellation_reason?.trim()) {
      toast.error('Please provide a cancellation reason')
      return
    }

    bulkUpdateMutation.mutate({
      data: {
        workflow_ids: selectedItems,
        ...(hasTransition && { transition: data.transition as any }),
        ...(data.transition === 'cancel_workflow' && { cancellation_reason: data.cancellation_reason }),
        ...(data.broker_ids_to_add.length > 0 && { broker_ids_to_add: data.broker_ids_to_add }),
        ...(data.broker_ids_to_remove.length > 0 && { broker_ids_to_remove: data.broker_ids_to_remove }),
        ...(hasAssignees && { force_reassign_all_tasks: data.force_reassign_all_tasks }),
      },
    }, {
      onSuccess: (data) => {
        // Update cached data with new workflows that include updated broker assignments.
        queryClient.setQueryData(
          getWorkflowsQueryKey,
          setQueryDataCallback(data)
        )
        toast.success(`${selectedItems.length} workflows successfully updated!`, {
          position: 'top-center',
        })
        handleOnOpenChange(false)
      },
    }
    )
  }

  const isValidRequest = (
    (assigneesToAdd.length > 0 || assigneesToRemove.length > 0 || selectedTransition) &&
    !assigneesValidationError
  )

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Dialog open={isOpen} onOpenChange={handleOnOpenChange}>
        <DialogContent className='flex flex-col max-h-[90vh] p-0'>
          <DialogHeader className='p-6'>
            <DialogTitle className='text-xl'>
              Update Selected Workflows
            </DialogTitle>
            <DialogDescription>
              {selectedItems.length} workflow{selectedItems.length === 1 ? '' : 's'} selected
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-4 flex-1 overflow-y-auto p-6'>
            <div className='space-y-4'>
              {/* Assignee Management Section */}
              <div className='space-y-4'>
                <div className='space-y-2'>
                  <Label>Add Assignees</Label>
                  <AssigneesFilter
                    selectedValues={assigneesToAdd}
                    onSelectValue={(brokerId) => toggleState('broker_ids_to_add', brokerId)}
                  />
                </div>

                <div className='space-y-2'>
                  <Label>Remove Assignees</Label>
                  <AssigneesFilter
                    selectedValues={assigneesToRemove}
                    onSelectValue={(brokerId) => toggleState('broker_ids_to_remove', brokerId)}
                  />
                </div>

                {assigneesValidationError && (
                  <div className='text-red-600 text-sm'>
                    {assigneesValidationError}
                  </div>
                )}

                {/* Force Reassign Checkbox */}
                <AnimateHeightAndOpacity isOpen={assigneesToAdd.length > 0 || assigneesToRemove.length > 0}>
                  <div className='flex items-center gap-2 pt-2'>
                    <Checkbox
                      id='force-reassign-all-tasks'
                      checked={forceReassignAll}
                      onCheckedChange={(checked) => setValue('force_reassign_all_tasks', checked === true)}
                    />
                    <label
                      htmlFor='force-reassign-all-tasks'
                      className='text-sm text-gray-700 cursor-pointer flex items-center gap-1 font-medium'
                    >
                      Override all task assignments
                      <TooltipWrapper
                        tooltipText="By default, only tasks that match the workflow's current broker(s)
                        will be reassigned. Check this to override ALL manual task assignments,
                        even those explicitly assigned to different brokers."
                        contentClasses='z-[60]'
                      >
                        <InfoCircledIcon className='w-4 h-4 text-gray-400' />
                      </TooltipWrapper>
                    </label>
                  </div>
                </AnimateHeightAndOpacity>
              </div>

              {/* State Transition Section */}
              <div className='space-y-2'>
                <Label htmlFor='transition-select'>Trigger Workflow State Change (Optional)</Label>
                <Select value={selectedTransition || ''} onValueChange={(value) => setValue('transition', value)}>
                  <SelectTrigger id='transition-select'>
                    <SelectValue placeholder='Select a workflow state change to trigger' />
                  </SelectTrigger>
                  <SelectContent>
                    {transitionOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className='flex flex-col justify-start items-start'>
                          <span className='font-medium'>{option.label}</span>
                          <span className='text-sm text-gray-500'>{option.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedTransition === 'cancel_workflow' && (
                <div className='space-y-2'>
                  <Label htmlFor='cancellation-reason'>Cancellation Reason</Label>
                  <Input
                    id='cancellation-reason'
                    value={cancellationReason || ''}
                    onChange={(e) => setValue('cancellation_reason', e.target.value)}
                    placeholder='Enter reason for cancellation'
                    required
                  />
                </div>
              )}
            </div>
          </div>

          <DialogFooter className='flex items-center justify-end mt-4 p-6'>
            <Button
              type='button'
              onClick={handleCancel}
              variant='secondary'
            >
              Cancel
            </Button>
            <Button
              disabled={selectedItems.length === 0 || !isValidRequest || bulkUpdateMutation.isPending}
              onClick={() => handleSubmit(onSubmit)()}
            >
              {bulkUpdateMutation.isPending
                ? (
                  <div className='flex items-center h-[100%]'>
                    <span>Updating...</span>
                    <TailSpin fill='#FFF' stroke='#FFF' className='h-[90%]' />
                  </div>
                  )
                : (
                    'Update'
                  )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  )
}

export default BulkUpdateWorkflowsDialog
